// One slide, drawn at 1600 by 900 in its deck's look, for every place a slide shows: the editor (its words typed in
// place), a thumbnail, full screen, the presenter's window, a shared link and a printed page. Each layout has its own
// type scale and spacing (shared/slides.ts TYPE), and its words shrink together to fit the slide, as Keynote's and
// Google Slides' do, never past its edge: measured here, once its fonts are in.

import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ImageIcon, LayoutPanelTop } from "lucide-react";
import { picturePath } from "../../../shared/imageInfo";
import { MIN_FIT, SLIDE_H, SLIDE_W, figureFit, type SlideData } from "../../../shared/slides";
import type { DocNode } from "../../../shared/docblocks";
import { SPEC } from "../../../shared/catalog";
import type { WidgetItem } from "../../../shared/types";
import { cx } from "../../lib/util";
import { useStore } from "../../state/store";
import type { DeckLook } from "./look";
import { BoxView, PlacedField, SlideBackground, fieldStyle, type BoxCtx } from "./Boxes";
import { byLayer, isPlaced, isThemeColor, type FieldName, type SlideBox } from "../../../shared/slideDesign";
import { DeckChrome } from "./theme/Chrome";
import { useSlideLook } from "./theme/ink";
import { buildState } from "../../../shared/slideMotion";
import { useMotionOpen } from "./motion/open";
import "../../styles/slides-motion.css";
import { moreAttrs } from "./place/words";
import { SlideNo } from "./place/ChromeSpot";
import { resolveSlide } from "../../../shared/slideMaster";

// ---------------------------------------------------------------------------
// a slide's words, drawn from its document (the editor draws the open slide's own)

/** A slide's title as drawn: its marked words where it has them (SA), else its plain words. */
const titleWords = (s: SlideData): ReactNode => (s.titleRuns?.length ? inline(s.titleRuns) : s.title);

function inline(nodes: DocNode[] | undefined): ReactNode[] {
  return (nodes ?? []).map((n, i) => {
    if (n.type === "hardBreak") return <br key={i} />;
    if (n.type !== "text") return <Fragment key={i}>{inline(n.content)}</Fragment>;
    let el: ReactNode = n.text;
    // a colour wraps the rest (SA), so an underline or a strike takes the words' own colour
    for (const m of [...(n.marks ?? [])].sort((x, y) => Number(x.type === "textStyle") - Number(y.type === "textStyle"))) {
      if (m.type === "bold") el = <strong>{el}</strong>;
      else if (m.type === "italic") el = <em>{el}</em>;
      // SA: underline, strikethrough and a colour on single words
      else if (m.type === "underline") el = <u>{el}</u>;
      else if (m.type === "strike") el = <s>{el}</s>;
      else if (m.type === "textStyle" && typeof m.attrs?.color === "string") el = <span style={{ color: isThemeColor(m.attrs.color) ? `var(--c-${m.attrs.color})` : m.attrs.color }}>{el}</span>;
      else if (m.type === "link" && typeof m.attrs?.href === "string" && /^(https?:|mailto:)/i.test(m.attrs.href))
        el = (
          <a href={m.attrs.href} target="_blank" rel="noopener noreferrer nofollow" onClick={(e) => e.stopPropagation()}>
            {el}
          </a>
        );
    }
    return <Fragment key={i}>{el}</Fragment>;
  });
}

function block(n: DocNode, i: number): ReactNode {
  switch (n.type) {
    case "paragraph":
      return <p key={i}>{n.content?.length ? inline(n.content) : <br />}</p>;
    case "bulletList":
      return <ul key={i}>{(n.content ?? []).map((it, j) => <li key={j}>{(it.content ?? []).map(block)}</li>)}</ul>;
    case "orderedList":
      return (
        <ol key={i} start={Number(n.attrs?.start) || undefined}>
          {(n.content ?? []).map((it, j) => (
            <li key={j}>{(it.content ?? []).map(block)}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

/** A slide's text, read only. */
export function BodyRead({ blocks, className, ...rest }: { blocks: DocNode[] | undefined; className?: string; style?: React.CSSProperties; "data-fieldbox"?: string }) {
  if (!blocks?.length) return null;
  return (
    <div className={cx("sl-text", className)} {...rest}>
      {blocks.map(block)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// fit

/** Fits measured, by what was measured: a thumbnail and the open slide of the same words agree, and measure once. */
const FITS = new Map<string, number>();
let fontsIn = typeof document === "undefined" || document.fonts?.status === "loaded";
const waiting = new Set<() => void>();
if (!fontsIn && typeof document !== "undefined")
  void document.fonts?.ready.then(() => {
    fontsIn = true;
    FITS.clear();
    for (const f of waiting) f();
  });

/**
 * Whether the words in the slide's pad run past it, at the fit set now: by where its boxes end, as laid out (a display
 * face's letters reach a little below their line, and that is no overflow), and in Two columns by each column's own.
 */
function over(pad: HTMLElement): boolean {
  const pr = pad.getBoundingClientRect();
  // the scale the slide is drawn at, so a thumbnail measures as the full slide does
  const k = pad.clientHeight ? pr.height / pad.clientHeight : 0;
  if (!k) return false;
  const outside = (box: HTMLElement) => {
    const br = box.getBoundingClientRect();
    return [...box.children].some((c) => {
      const r = c.getBoundingClientRect();
      return (r.bottom - br.top) / k > box.clientHeight + 1 || (r.top - br.top) / k < -1;
    });
  };
  return outside(pad) || [...pad.querySelectorAll<HTMLElement>(".sl-col")].some(outside);
}

/**
 * The fit a slide's words need, measured on its pad: 1 when they fit, else the largest that does, down to MIN_FIT, by
 * halving the gap seven times (to within a percent). The fit is set on the face as it goes, so the words are drawn at it.
 */
export function measureFit(face: HTMLElement): number {
  const pad = face.querySelector<HTMLElement>(".sl-pad");
  if (!pad) return 1;
  const set = (f: number) => face.style.setProperty("--fit", String(f));
  set(1);
  if (!over(pad)) return 1;
  let lo = MIN_FIT;
  let hi = 1;
  set(lo);
  if (over(pad)) return lo;
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2;
    set(mid);
    if (over(pad)) hi = mid;
    else lo = mid;
  }
  set(lo);
  return Math.floor(lo * 100) / 100;
}

/** The figure's own fit: as large as the type scale says, smaller when it would run past the slide's width. */
function measureFigure(face: HTMLElement) {
  const fig = face.querySelector<HTMLElement>(".sl-figure");
  if (!fig) return;
  fig.style.setProperty("--fig", "1");
  if (fig.scrollWidth > fig.clientWidth + 1) fig.style.setProperty("--fig", String(Math.max(0.2, (fig.clientWidth / fig.scrollWidth) * 0.98)));
}

// ---------------------------------------------------------------------------
// the face

export type FaceMode = "edit" | "show" | "thumb" | "print";

export interface FaceProps {
  slide: SlideData;
  look: DeckLook;
  /** its number in the deck, shown small on the slides that carry one */
  number?: number;
  /** a Section's own number among the deck's sections */
  section?: number;
  mode: FaceMode;
  /** the editor's fields, in place of the read-only words (edit mode) */
  fields?: Partial<Record<"title" | "body" | "body2" | "figure" | "cite", ReactNode>>;
  /** the picture's place, when the slide has none (edit mode: drop, paste or pick one) */
  pictureSlot?: ReactNode;
  /** the widget's place, when the slide has none (edit mode) */
  widgetSlot?: ReactNode;
  /** how the widget is drawn when there is one: live (a tile), or as a card naming it (thumbnails) */
  renderWidget?: (item: WidgetItem) => ReactNode;
  /** told the fit once it is measured */
  onFit?: (fit: number) => void;
  /** words that change while typing, so the fit is measured again */
  measureKey?: string;
  className?: string;
  /** the editor's words for the box being typed in (edit mode) */
  boxEditor?: (box: SlideBox) => ReactNode | undefined;
  /** presenting (S7): how many of its builds show (absent: the slide as it ends), and whether the last came in just now */
  step?: number;
  fresh?: boolean;
}

/** The slides a deck numbers: everything but a title, a section, a full picture and a blank one. */
const NUMBERED = new Set(["text", "two", "big", "quote", "widget"]);

export function SlideFace({ slide: own, look, number, section, mode, fields = {}, pictureSlot, widgetSlot, renderWidget, onFit, measureKey, className, boxEditor, step, fresh = false }: FaceProps) {
  const ref = useRef<HTMLDivElement>(null);
  // SC: the slide as it follows its layout and the deck's master, its own pieces winning (shared/slideMaster.ts)
  const { under = [], ...s } = resolveSlide(own, look, look.masterSkip);
  // its background, its variables and its theme colours, with words that read on that background (S6)
  const { bg, vars, palette } = useSlideLook(look, s, mode);
  const item = useStore((st) => (s.widget && st.items[s.widget]?.kind === "widget" ? (st.items[s.widget] as WidgetItem) : null));
  const key = `${s.layout}|${look.titleFont}|${look.titleScale}|${measureKey ?? ""}|${s.title}|${JSON.stringify(s.body)}|${JSON.stringify(s.body2 ?? null)}|${s.figure ?? ""}|${s.cite ?? ""}|${s.image ? 1 : 0}|${s.place ? JSON.stringify(s.place) : ""}`;
  const [, redraw] = useState(0);
  useLayoutEffect(() => {
    const face = ref.current;
    if (!face) return;
    const known = mode !== "edit" ? FITS.get(key) : undefined;
    let fit: number;
    if (known !== undefined) {
      face.style.setProperty("--fit", String(known));
      fit = known;
    } else {
      fit = measureFit(face);
      if (fontsIn) {
        FITS.set(key, fit);
        if (FITS.size > 600) FITS.delete(FITS.keys().next().value!);
      }
    }
    measureFigure(face);
    onFit?.(fit);
    if (!fontsIn) {
      const again = () => redraw((n) => n + 1);
      waiting.add(again);
      return () => void waiting.delete(again);
    }
  });

  const editing = mode === "edit";
  const has = (k: "title" | "body" | "body2" | "figure" | "cite") => editing && fields[k] !== undefined;

  // the slide's own design (S5): boxes and the fields moved out of the layout share one order of layers, those below 0
  // under the layout's own words, the rest over them
  const place = s.place ?? {};
  const theme = { title: look.titleFont, body: look.bodyFont };
  const boxes = s.boxes ?? [];
  const placedZ = (Object.keys(place) as FieldName[]).filter((f) => isPlaced(place[f]) && !place[f]!.hidden).map((f) => ({ z: place[f]!.z ?? 0, key: `f:${f}` }));
  const order = [...boxes.map((b) => ({ z: b.z, key: `b:${b.id}` })), ...placedZ].sort((a, b) => a.z - b.z || (a.key < b.key ? -1 : 1));
  const backs = order.filter((o) => o.z < 0).length;
  const zIndex = new Map(order.map((o, i) => [o.key, i < backs ? i + 1 : i + 2]));
  const designed = boxes.length > 0 || placedZ.length > 0 || !!bg || under.length > 0;
  // what is still to come on a click, and what just came in (S7)
  const built = buildState(s.builds, step, fresh);
  const motionOpen = useMotionOpen((m) => m.open) && mode === "edit";
  const tags = new Map(motionOpen ? (s.builds ?? []).map((b, i) => [b.id, i + 1] as const) : []);
  const ctx: BoxCtx = { palette, theme, mode, renderWidget, editorFor: boxEditor, layer: () => 0, ...(built.size ? { build: (id: string) => built.get(id) } : {}), ...(tags.size ? { buildTag: (id: string) => tags.get(id) } : {}) };
  const placed: ReactNode[] = [];
  /** A field where its layout puts it, else where it was placed (drawn with the boxes), or nothing when deleted. */
  const slot = (name: FieldName, el: ReactNode): ReactNode => {
    const p = place[name];
    if (p?.hidden || el === null || el === undefined || el === false) return null;
    if (isPlaced(p)) {
      placed.push(
        <PlacedField key={name} field={name} place={p} z={zIndex.get(`f:${name}`) ?? 1}>
          {el}
        </PlacedField>,
      );
      return null;
    }
    return el;
  };
  const fx = (name: FieldName) => ({ style: fieldStyle(place[name], palette, theme), ...moreAttrs(place[name]), "data-fieldbox": name, "data-build": built.get(`f:${name}`), "data-build-n": tags.get(`f:${name}`) });
  // with no title to sit over, the rule is left off wherever the slide is shown (the editor keeps it, where the title goes)
  const rule = editing || s.title ? <span className="sl-rule" aria-hidden /> : null;
  // the accent's short rule belongs to the title: moved out of its layout, the title takes it along
  const titleMoved = isPlaced(place.title) || !!place.title?.hidden;
  // a slide brought from a PowerPoint file draws as it was made: no rule of the board's (S7)
  const ruleHere = titleMoved || s.plain ? null : rule;
  const titleEl = (cls: string) => (has("title") ? <div className={cx("sl-title", cls)} {...fx("title")}>{fields.title}</div> : s.title ? <h2 className={cx("sl-title", cls)} {...fx("title")}>{titleWords(s)}</h2> : null);
  const title = (cls: string, ruled = false) => {
    const el = titleEl(cls);
    return slot("title", ruled && el && isPlaced(place.title) && !s.plain ? <div className="sl-ruled">{rule}{el}</div> : el);
  };
  const body = (cls: string) => slot("body", has("body") ? <div className={cx("sl-body", cls)} {...fx("body")}>{fields.body}</div> : s.body?.content.length ? <BodyRead blocks={s.body?.content} className={cx("sl-body", cls)} {...fx("body")} /> : null);
  const picture = s.image ? <img className="sl-pic-img" src={picturePath(s.image.key)} alt="" draggable={false} loading={mode === "thumb" ? "lazy" : "eager"} decoding="async" /> : editing ? pictureSlot : <span className="sl-pic-none" aria-hidden><ImageIcon size={64} strokeWidth={1.25} /></span>;
  const widget = item ? (mode === "thumb" || !renderWidget ? <WidgetCard item={item} /> : renderWidget(item)) : editing ? widgetSlot : <span className="sl-pic-none" aria-hidden><LayoutPanelTop size={64} strokeWidth={1.25} /></span>;

  let inner: ReactNode = null;
  switch (s.layout) {
    case "title":
      inner = (
        <div className="sl-pad">
          <div className="sl-stack is-bottom">
            {ruleHere}
            {title("is-hero", true)}
            {body("is-sub")}
          </div>
        </div>
      );
      break;
    case "section":
      inner = (
        <div className="sl-pad">
          <div className="sl-stack is-bottom">
            {section ? <span className="sl-section-no">{String(section).padStart(2, "0")}</span> : rule}
            {title("is-section")}
            {body("is-sub is-small")}
          </div>
        </div>
      );
      break;
    case "two":
      inner = (
        <div className="sl-pad">
          {ruleHere}
          {title("is-head", true)}
          <div className="sl-cols">
            <div className="sl-col">{body("is-col")}</div>
            <div className="sl-col">{slot("body2", has("body2") ? <div className="sl-body is-col" {...fx("body2")}>{fields.body2}</div> : s.body2?.content.length ? <BodyRead blocks={s.body2?.content} className="sl-body is-col" {...fx("body2")} /> : null)}</div>
          </div>
        </div>
      );
      break;
    case "big":
      inner = (
        <div className="sl-pad">
          <div className="sl-stack is-middle">
            {slot("title", has("title") ? <div className="sl-eyebrow" {...fx("title")}>{fields.title}</div> : s.title ? <h2 className="sl-eyebrow" {...fx("title")}>{titleWords(s)}</h2> : null)}
            {slot(
              "figure",
              has("figure") ? (
                <div className="sl-figure" data-fieldbox="figure" data-build={built.get("f:figure")} style={{ ["--fig0" as string]: String(figureFit(s.figure ?? "")), ...fieldStyle(place.figure, palette, theme) }}>
                  {fields.figure}
                </div>
              ) : s.figure ? (
                <div className="sl-figure" data-fieldbox="figure" data-build={built.get("f:figure")} style={{ ["--fig0" as string]: String(figureFit(s.figure)), ...fieldStyle(place.figure, palette, theme) }}>
                  {s.figure}
                </div>
              ) : null,
            )}
            {body("is-meaning")}
          </div>
        </div>
      );
      break;
    case "quote":
      inner = (
        <div className="sl-pad">
          {slot("title", has("title") ? <div className="sl-eyebrow is-top" {...fx("title")}>{fields.title}</div> : s.title ? <h2 className="sl-eyebrow is-top" {...fx("title")}>{titleWords(s)}</h2> : null)}
          <div className="sl-stack is-middle">
            <svg className="sl-quote-mark" viewBox="0 0 120 92" aria-hidden>
              <path d="M27 92C12 92 2 81 2 66c0-27 17-52 45-66l7 10C37 21 29 34 28 46c15 1 25 11 25 23 0 13-11 23-26 23Zm64 0c-15 0-25-11-25-26 0-27 17-52 45-66l7 10c-17 11-25 24-26 36 15 1 25 11 25 23 0 13-11 23-26 23Z" />
            </svg>
            {body("is-quote")}
            {slot("cite", has("cite") ? <div className="sl-cite" {...fx("cite")}>{fields.cite}</div> : s.cite ? <p className="sl-cite" {...fx("cite")}>{s.cite}</p> : null)}
          </div>
        </div>
      );
      break;
    case "picture":
      inner = (
        <>
          {slot("image", <div className="sl-pic is-half" data-fieldbox="image" data-build={built.get("f:image")}>{picture}</div>)}
          <div className="sl-pad is-left">
            {ruleHere}
            {title("is-side", true)}
            {body("is-side")}
          </div>
        </>
      );
      break;
    case "full": {
      const words = !!(s.title || s.body?.content.length || has("title"));
      inner = (
        <>
          {slot("image", <div className="sl-pic is-full" data-fieldbox="image" data-build={built.get("f:image")}>{picture}</div>)}
          {words && <div className="sl-shade" aria-hidden />}
          <div className="sl-pad is-over">
            <div className="sl-stack is-bottom">
              {title("is-full")}
              {body("is-caption")}
            </div>
          </div>
        </>
      );
      break;
    }
    case "widget":
      inner = (
        <div className="sl-pad is-widget">
          {ruleHere}
          {title("is-head", true)}
          {slot("widget", <div className="sl-widget" data-fieldbox="widget" data-build={built.get("f:widget")}>{widget}</div>)}
        </div>
      );
      break;
    case "blank":
      inner = <div className="sl-pad" />;
      break;
    default:
      inner = (
        <div className="sl-pad">
          {ruleHere}
          {title("is-head", true)}
          {body("is-main")}
        </div>
      );
  }
  // a slide as it was made draws as it always has; one with a design of its own puts its layout on a layer of its own
  return (
    <div ref={ref} className={cx("sl-face", `is-${s.layout}`, `is-${mode}`, designed && "is-designed", className)} style={{ ...vars, width: SLIDE_W, height: SLIDE_H } as React.CSSProperties} data-layout={s.layout}>
      {bg && <SlideBackground bg={bg} palette={palette} />}
      {/* SC: the master's and the layout's own boxes, behind everything on the slide, never picked on it */}
      {under.length > 0 && (
        <div className="sl-master-under" aria-hidden>
          {byLayer(under).map((b) => (
            <BoxView key={b.id} box={b} ctx={{ ...ctx, mode: mode === "edit" ? "show" : mode, editorFor: undefined, build: undefined, buildTag: undefined }} />
          ))}
        </div>
      )}
      {designed ? (
        <div className="sl-layout" style={{ zIndex: backs + 1 }}>
          {inner}
        </div>
      ) : (
        inner
      )}
      {placed}
      {byLayer(boxes).map((b) => (
        <BoxView key={b.id} box={b} ctx={{ ...ctx, layer: () => zIndex.get(`b:${b.id}`) ?? 1 }} />
      ))}
      {look.custom ? <DeckChrome chrome={s.noMaster && look.chrome ? { ...look.chrome, logo: undefined } : look.chrome} layout={s.layout} number={number} over={s.layout === "full"} ground={bg} /> : number && NUMBERED.has(s.layout) && <SlideNo number={number} ground={bg} />}
    </div>
  );
}

/** A widget on a slide in small: its kind's look and its title, never numbers it has not drawn (a thumbnail is no place for live data). */
export function WidgetCard({ item }: { item: WidgetItem }) {
  return (
    <div className="sl-widget-card">
      <span className="sl-widget-card-kind">{SPEC[item.type]?.label ?? "Widget"}</span>
      <b>{item.title || SPEC[item.type]?.label}</b>
      <i aria-hidden />
      <i aria-hidden />
    </div>
  );
}
